/**
 *  @fileOverview Image loader module
 *
 *  @author       Daniel Jung <daniel.jung@possible.com>
 *
 *  @requires     NPM:lodash
 *  @requires     NPM:in-view
 */

import forEach from 'lodash/forEach';
import throttle from 'lodash/throttle';
import detectIt from 'detect-it';
import deviceProperties from '../utils/device-properties';
import WithinViewport from '../utils/within-viewport';

const URL = window.location.hostname.match(/localhost|10\.|127\./ig)
  ? 'https://futurefocus-staging.staff.hsbc.co.uk/'
  : '';
const TOKEN = document.body && document.body.getAttribute('data-token');
const TOKEN_MODE = 'header';

const RANGES = [
  320, 480, 568, 640, 720, 768,
  800, 960, 1080, 1200, 1366,
  1440, 1536, 1600, 1720, 1920,
  2048, 2160, 2560, 2880, 3000,
  3200, 3600, 3840, 4000, 5120,
];

const isIE = () => (
  /MSIE 10/i.test(navigator.userAgent) ||
  /MSIE 9/i.test(navigator.userAgent) ||
  /rv:11.0/i.test(navigator.userAgent)
);

class ImageLoader {
  init() {
    this.devicePixelRatio = deviceProperties.pixelRatio;
    this.screenWidth = deviceProperties.window.width;
    this.activeXHRs = [];
    this.initBindings();
    this.checkAndLoad();
  }

  initBindings() {
    window.addEventListener('scroll', throttle(() => {
      this.checkAndLoad();
    }, 50), detectIt.passiveEvents ? { capture: false, passive: false } : false);

    window.addEventListener('resize', () => {
      const proportion = 1.2;
      if (this.screenWidth * proportion < window.innerWidth) {
        this.screenWidth = window.innerWidth;
        this.checkAndLoad();
      }
    });
  }

  checkAndLoad() {
    forEach(document.body.querySelectorAll('[data-img-src]:not([data-img-loaded])'), (el) => {
      el.removeAttribute('data-img-loaded');
      const imgSrc = el.getAttribute('data-img-src');
      const withinViewport = new WithinViewport(el, {
        container: document.body,
        sides: 'top bottom',
        bottom: -200,
      });
      const imageVisible = withinViewport.isWithin();
      if (imageVisible) {
        this.updateImage(el, imgSrc);
      }
    });
  }

  viewportHandler(element) {
    const imageSrc = element.getAttribute('data-img-src');
    if (element.hasAttribute('data-img-loaded')) {
      return false;
    }
    this.updateImage(element, imageSrc);
    return true;
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
          img.src = window.URL.createObjectURL(xhr.response);
        }
      };

      img.onload = () => {
        resolve('Image loaded');
      };

      img.onerror = () => {
        reject(new Error('Could not load image'));
      };

      if (isIE()) {
        if (TOKEN_MODE === 'header') {
          xhr.open('GET', src, true);
        } else {
          img.src = src;
        }
      } else if (TOKEN_MODE === 'header') {
        xhr.open('GET', src, true);
      } else {
        img.src = src;
      }

      if (TOKEN_MODE === 'header') {
        if (this.getXHRBySrc(src) === null || this.getXHRBySrc(src) === src) {
          xhr.responseType = 'blob';
          xhr.withCredentials = true;
          xhr.setRequestHeader('token', TOKEN);
          xhr.send();
          this.activeXHRs.push({ xhr, src });
        }
      }
    });
  }

  updateImage(element, imageSrc) {
    let width;

    if (isIE() && element.tagName.toLowerCase() === 'img') {
      width = Math.floor(element.parentNode.clientWidth * 1.1 * this.devicePixelRatio);
    } else {
      width = Math.floor(element.clientWidth * 1.1 * this.devicePixelRatio);
    }

    width = this.getClosestSize(width);

    let updatedImageSrc = `${imageSrc.split('?')[0]}?width=${width}&quality=70`;

    if (TOKEN_MODE !== 'header') {
      updatedImageSrc += `&token=${TOKEN}`;
    }

    if (updatedImageSrc.match(/futurefocus.possible.hu|futurefocus-staging/) === null && URL !== '') {
      updatedImageSrc = `${URL}${updatedImageSrc}`;
    }

    this.loadImage(updatedImageSrc)
      .then(() => {
        element.setAttribute('src', updatedImageSrc);
        element.setAttribute('data-img-loaded', 'true');
      })
      .catch(() => {
        if (element.getAttribute('data-img-loaded') === null) {
          element.setAttribute('src', '');
          element.setAttribute('data-img-loaded', 'true');
          element.setAttribute('data-fail-src', imageSrc.split('?')[0]);
          console.warn(`Cannot load image: ${updatedImageSrc}`);
        }
      });
  }

  refreshImage() {
    forEach(document.body.querySelectorAll('[data-img-src]:not([data-img-loaded])'), (el) => {
      const imgSrc = el.getAttribute('data-img-src');
      this.updateImage(el, imgSrc);
    });
  }

  getClosestSize(originalSize) {
    let selectedSize = false;

    forEach(RANGES, (range) => {
      if (originalSize < range && !selectedSize) {
        selectedSize = range;
      }
    });
    return selectedSize;
  }

  getXHRBySrc(src) {
    const result = this.activeXHRs.filter(xhr => (xhr.src === src));

    if (result && result[0] && result[0].src) {
      return result[0].src;
    }
    return null;
  }
}

export const imageLoader = new ImageLoader();



// WEBPACK FOOTER //
// ./src/js/modules/image-loader.js